import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Button, Card, EmptyState, IconDownload, IconSearch, LoadingList, MultiSelect, PageHeader, SelectionBar,
    cx, fieldClass, filterLabelClass, type MultiOption,
} from '@timely/ui';
import {
    AGEING_FILTER_LABELS, BAD_DEBT_MEANING, BULK_LIMIT, CONTACT_FILTER_LABELS, DEFAULT_FILTERS, DEFAULT_SORT, FOLLOW_UP_FILTERS,
    PROMISE_FILTERS, SETTLEMENT_LABELS, SORT_LABELS,
    RANK_WORDS, activeFilterCount, bookRow, bookTotals, csvFileName, filtersFromParams, holds, isOneAgeRow, joinList, splitList,
    writeFilterParams,
    type BookFilters, type BookSort, type BulkAction, type ContactFilter, type SettlementFilter,
} from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { CONCENTRATION_TOP, matchingIds, useBook, useCategories, useConcentration, useLastNotes, type ServerTotals } from '../book/useBook';
import { useFormatters } from '../book/useFormatters';
import { AccountTable, assigneeName } from '../book/AccountTable';
import { BulkChangeDialog } from '../book/BulkChangeDialog';
import { ExportError, downloadCsv, exportCustomers } from '../reports/useReports';
import { paths } from '../workspace/navigation';
import { SourceFreshness } from '../receivables/SourceFreshness';

/**
 * Customers: every account this person may see — the master list the day's
 * work is drawn from (PRODUCT-UX-FOUNDATION §8).
 *
 * It opens on what is work (accounts with dues), a row is one press away from
 * the account, and the filters sit on the list they narrow. The filters, the
 * sort and the paging are all arguments to `book_page`, so the list is of the
 * whole filtered book and the headline is the aggregate over that same
 * population — never of the page on screen. Which accounts a person may see at
 * all is decided by `can_see_customer()` in SQL, on the page and the aggregate
 * alike.
 *
 * Filters live in the address bar, so a back press from an account returns to
 * the list as it was, and a filtered list can be sent to a colleague. Three of
 * them take several values at once (0033): follow-up, whose, and rank.
 *
 * Somebody who may change many accounts at once, or export, can choose rows —
 * or every account the filters match, up to the limit the database enforces —
 * and act on them together. Each change is checked by the database before it
 * is confirmed, account by account (`BulkChangeDialog`).
 */
export function Book() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const { money, count, date, today } = useFormatters();
    const to = paths(workspace.slug);
    const wholeBook = holds(context, 'customers.view.all');

    const canBulk = holds(context, 'customers.bulk');
    const canExport = holds(context, 'reports.export');
    const canReassign = canBulk && holds(context, 'customers.edit') && holds(context, 'customers.assign');
    const canDate = canBulk && holds(context, 'followups.record');
    const canRank = canBulk && holds(context, 'customers.edit');
    const canChoose = canReassign || canDate || canRank || canExport;

    const [params, setParams] = useSearchParams();
    const filters = filtersFromParams(params);
    const sort = (params.get('sort') as BookSort) ?? DEFAULT_SORT;

    const {
        customers, members, totals: serverTotals, loading, loadingMore, error,
        reload, loadMore, hasMore, loadedCount,
    } = useBook(context, { filters, sort });
    const categories = useCategories(workspace.id);

    const memberNames = useMemo(
        () => new Map(members.map(m => [m.memberId, m.displayName || m.code || 'Somebody'])),
        [members],
    );

    // Presentation only: the rows the database returned, worked out against the
    // company's day (the database filtered by it too). No filtering and no
    // sorting here — both already happened, over accounts this screen never saw.
    const rows = useMemo(() => customers.map(c => bookRow(c, today)), [customers, today]);
    const onScreen = useMemo(() => bookTotals(rows), [rows]);
    const lastNotes = useLastNotes(workspace.id, useMemo(() => customers.map(c => c.id), [customers]));

    // What is chosen belongs to the question it was chosen from: a new filter
    // lets go, so a change can never reach accounts no longer on screen.
    const search = params.toString();
    const [chosen, setChosen] = useState<Set<string>>(() => new Set());
    useEffect(() => { setChosen(new Set()); }, [search]);

    const [bulk, setBulk] = useState<BulkAction | null>(null);
    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [working, setWorking] = useState<'choosing' | 'exporting' | null>(null);

    const set = (changes: Partial<BookFilters> & { sort?: BookSort }) =>
        setParams(writeFilterParams(params, changes), { replace: true });

    const narrowed = activeFilterCount(filters);
    const clear = () => setParams(new URLSearchParams(), { replace: true });
    // A fact about the whole book, so only over the whole book: never beside a filtered list it does not describe.
    const concentrated = useConcentration(workspace.id, narrowed === 0);

    const chooseAll = async () => {
        setWorking('choosing');
        setNotice(null);
        try {
            const ids = await matchingIds(workspace.id, filters, sort);
            setChosen(new Set(ids));
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof Error ? failed.message : 'That did not work.' });
        } finally {
            setWorking(null);
        }
    };

    const runExport = async (ids: readonly string[] | null) => {
        setWorking('exporting');
        setNotice(null);
        try {
            const result = await exportCustomers(workspace.id, filters, workspace.currency, undefined, ids);
            if (result.rowCount === 0) {
                setNotice({ tone: 'error', text: 'There is nothing to export.' });
                return;
            }
            downloadCsv(csvFileName('customers', workspace.name, date.todayValue()), result.csv);
            setNotice({
                tone: 'ok',
                text: result.capped
                    ? `Exported the largest ${count(result.rowCount)} accounts. Narrow the list and export again for the rest.`
                    : `Exported ${count(result.rowCount)} ${result.rowCount === 1 ? 'account' : 'accounts'}${ids ? ' you chose' : ''}.`,
            });
        } catch (failed) {
            setNotice({ tone: 'error', text: failed instanceof ExportError ? failed.message : 'That export did not work.' });
        } finally {
            setWorking(null);
        }
    };

    const matched = serverTotals?.matchedCount ?? loadedCount;

    return (
        <Page width="wide">
            <PageHeader
                title={wholeBook ? 'Customers' : 'My customers'}
                subtitle="Every account you can see, what is owed, and what happens next."
                actions={
                    <div className="flex items-end gap-4 flex-wrap justify-end">
                        {canExport && matched > 0 && (
                            <Button variant="secondary" size="sm" busy={working === 'exporting'} disabled={working !== null}
                                onClick={() => { void runExport(null); }}>
                                <IconDownload size={14} />{working === 'exporting' ? 'Building…' : narrowed > 0 ? 'Export this list' : 'Export'}
                            </Button>
                        )}
                        <BookSummary
                            serverTotals={serverTotals}
                            onScreen={{ count: onScreen.count, outstanding: onScreen.outstanding }}
                            loadedCount={loadedCount}
                            loading={loading}
                        />
                    </div>
                }
            />

            {/* How old these figures are, before anybody acts on them (Phase 0). */}
            <SourceFreshness className="-mt-2 mb-4" />

            {/* Where the money sits (Customer 360, 02 §7): one fixed sentence over the book's own figures, and the door
                to the accounts it counts. */}
            {concentrated && (
                <p className="-mt-1 mb-4 text-secondary text-label-2">
                    The {count(CONCENTRATION_TOP)} largest accounts hold{' '}
                    <strong className="font-semibold text-label num" title={money.exact(concentrated.amount)}>{concentrated.percent}</strong> of what is owed.{' '}
                    <Link to={`${to.customers}?settlement=owes&sort=amount-desc`} className="font-semibold text-accent-text hover:underline">See them</Link>
                </p>
            )}

            {error && <div className="mb-4"><FormNotice tone="error">{error}</FormNotice></div>}
            {notice && <div className="mb-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}

            <Card className="overflow-hidden">
                <Filters
                    filters={filters}
                    sort={sort}
                    members={members}
                    categories={categories}
                    showAssignee={wholeBook}
                    narrowed={narrowed}
                    onChange={set}
                    onClear={clear}
                />

                {chosen.size > 0 && (
                    <SelectionBar
                        count={chosen.size}
                        noun="account"
                        total={Math.min(matched, BULK_LIMIT)}
                        selectAllLabel={working === 'choosing' ? 'Choosing…'
                            : matched > BULK_LIMIT ? `Choose the first ${count(BULK_LIMIT)} of ${count(matched)}` : `Choose all ${count(matched)}`}
                        onSelectAll={() => { void chooseAll(); }}
                        onClear={() => setChosen(new Set())}
                    >
                        {canReassign && <Button size="sm" variant="secondary" onClick={() => setBulk('collector')}>Reassign</Button>}
                        {canDate && <Button size="sm" variant="secondary" onClick={() => setBulk('follow_up')}>Set follow-up date</Button>}
                        {canRank && <Button size="sm" variant="secondary" onClick={() => setBulk('rank')}>Set rank</Button>}
                        {canExport && (
                            <Button size="sm" variant="secondary" busy={working === 'exporting'} disabled={working !== null}
                                onClick={() => { void runExport([...chosen]); }}>
                                <IconDownload size={14} />Export chosen
                            </Button>
                        )}
                    </SelectionBar>
                )}
                {/* What the rank means, where somebody is looking at it (never "written off"). */}
                {splitList(filters.rank).includes('Bad') && (
                    <p role="note" className="px-4 py-2 border-b border-separator text-caption text-label-3">
                        <strong className="font-semibold text-label-2">Bad debt:</strong> {BAD_DEBT_MEANING}
                    </p>
                )}
                {onScreen.count > 0 && (
                    <div className="px-4 py-2 border-b border-separator flex items-center gap-x-5 gap-y-1 flex-wrap text-caption text-label-3">
                        {/* Of the rows on screen, and it says so: the headline above is the claim about the book. */}
                        <span>Of the {count(onScreen.count)} shown:</span>
                        {/* An age, and a count of follow-ups: neither is money past its due date (Customer 360, F1, F2). */}
                        <span><strong className="text-label num">{money.compact(onScreen.over90)}</strong> more than 90 days old</span>
                        <span><strong className="text-label num">{count(onScreen.dueToday)}</strong> due today</span>
                        <span><strong className="text-label num">{count(onScreen.overdue)}</strong> late {onScreen.overdue === 1 ? 'follow-up' : 'follow-ups'}</span>
                        {onScreen.unassigned > 0 && (
                            <span><strong className="text-label num">{count(onScreen.unassigned)}</strong> unassigned</span>
                        )}
                    </div>
                )}

                <div>
                    {loading ? (
                        <LoadingList label="Loading customers" rows={8} />
                    ) : error ? (
                        <div className="px-5 py-6">
                            <Button variant="secondary" onClick={() => { void reload(); }}>Try again</Button>
                        </div>
                    ) : serverTotals !== null && serverTotals.visibleCount === 0 ? (
                        <EmptyState
                            title="No customers yet"
                            hint={holds(context, 'datasource.manage')
                                ? 'Connect TallyPrime or your receivables sheet, and your customers appear here.'
                                : 'Once somebody connects TallyPrime or your receivables sheet, your customers appear here.'}
                            action={holds(context, 'datasource.manage')
                                ? <Link to={to.setting('data')} className="text-accent-text font-semibold hover:underline">Connect your receivables data</Link>
                                : undefined}
                        />
                    ) : rows.length === 0 ? (
                        <EmptyState
                            title="Nothing matches"
                            hint={serverTotals
                                ? `None of your ${count(serverTotals.visibleCount)} accounts match what you are looking for.`
                                : 'No account matches what you are looking for.'}
                            action={<Button variant="secondary" onClick={clear}>Clear filters</Button>}
                        />
                    ) : (
                        <AccountTable
                            rows={rows}
                            label="Customers"
                            showAssignee={wholeBook}
                            lastNotes={lastNotes}
                            linkTo={id => `${to.customer(id)}${search ? `?${search}` : ''}`}
                            assigneeOf={row => assigneeName(row, memberNames)}
                            {...(canChoose ? {
                                selection: {
                                    chosen,
                                    onToggle: (id: string, on: boolean) => setChosen(current => {
                                        const next = new Set(current);
                                        if (on) next.add(id); else next.delete(id);
                                        return next;
                                    }),
                                    onToggleShown: (on: boolean) => setChosen(current => {
                                        const next = new Set(current);
                                        for (const row of rows) { if (on) next.add(row.customer.id); else next.delete(row.customer.id); }
                                        return next;
                                    }),
                                },
                            } : {})}
                        />
                    )}
                </div>
                {hasMore && !loading && (
                    <div className="px-4 py-3 border-t border-separator flex justify-center">
                        <Button variant="secondary" disabled={loadingMore} onClick={() => { void loadMore(); }} busy={loadingMore}>
                            {loadingMore ? 'Loading…' : serverTotals
                                ? `Load more (${count(loadedCount)} of ${count(serverTotals.matchedCount)})`
                                : `Load more (${count(loadedCount)} so far)`}
                        </Button>
                    </div>
                )}

            </Card>

            {bulk && (
                <BulkChangeDialog
                    workspaceId={workspace.id}
                    ids={[...chosen]}
                    action={bulk}
                    members={members}
                    onClose={() => setBulk(null)}
                    onDone={async message => {
                        setBulk(null);
                        setChosen(new Set());
                        await reload();
                        setNotice({ tone: 'ok', text: message });
                    }}
                />
            )}
        </Page>
    );
}

/* -------------------------------------------------------------------------- */

const FOLLOW_UP_OPTIONS: MultiOption[] = [
    ...FOLLOW_UP_FILTERS.map(f => ({ value: f.value, label: f.label, group: 'Next follow-up' })),
    ...PROMISE_FILTERS.map(p => ({ value: p.value, label: p.label, group: 'Promise' })),
];

const RANK_OPTIONS: MultiOption[] = [
    { value: 'Good', label: RANK_WORDS.Good },
    { value: 'Late', label: RANK_WORDS.Late },
    { value: 'Bad', label: RANK_WORDS.Bad },
];

/**
 * The filters, as one bar on the list they narrow (DESIGN-SYSTEM §7.4).
 *
 * Search first and widest; then what people ask most — which accounts, what is
 * next, how old, whose, in what order — in one row on a desktop. "Showing" is
 * out in the open (it was folded away, and it is the first question). A filter
 * that is narrowing the list is marked on the control itself, and one button
 * clears them all. What is asked less often — category, rank, last contact,
 * urgency — folds away under More filters. On a phone only the search stays:
 * the rest fold behind one button that says how many are set, so the first
 * account is on the first screen.
 */
function Filters({
    filters, sort, members, categories, showAssignee, narrowed, onChange, onClear,
}: {
    filters: BookFilters;
    sort: BookSort;
    members: { memberId: string; displayName: string; code: string | null }[];
    categories: { category: string; count: number }[];
    showAssignee: boolean;
    narrowed: number;
    onChange: (changes: Partial<BookFilters> & { sort?: BookSort }) => void;
    onClear: () => void;
}) {
    const folded = [
        filters.rank !== DEFAULT_FILTERS.rank, filters.urgentOnly, filters.category.length > 0, filters.contact !== 'any',
    ].filter(Boolean).length;
    const [open, setOpen] = useState(folded > 0);
    const [phoneOpen, setPhoneOpen] = useState(false);
    const set = narrowed - (filters.search.trim() ? 1 : 0);

    const ownerOptions: MultiOption[] = [
        { value: 'mine', label: 'Me' },
        { value: 'unassigned', label: 'Nobody' },
        ...members.map(m => ({ value: m.memberId, label: m.displayName || m.code || 'Somebody' })),
    ];
    // Commonest first, and "Not set" last, where people look for the leftovers.
    const categoryOptions: MultiOption[] = [...categories]
        .sort((a, b) => Number(a.category === '') - Number(b.category === ''))
        .map(c => ({ value: c.category, label: c.category || 'Not set', count: c.count }));
    // A category in the address that no account carries any more still shows, so it can be cleared.
    for (const value of filters.category) {
        if (!categoryOptions.some(o => o.value === value)) categoryOptions.push({ value, label: value || 'Not set' });
    }

    return (
        <div className="border-b border-separator">
            <div className="px-4 py-3 flex items-end gap-2.5 flex-wrap">
                <div className="flex-1 min-w-48 md:max-w-sm">
                    <label htmlFor="book-search" className={filterLabelClass}>Search</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-label-3 pointer-events-none"><IconSearch /></span>
                        <input
                            id="book-search"
                            type="search"
                            data-shortcut-search
                            value={filters.search}
                            placeholder="Name, code, phone or city"
                            aria-keyshortcuts="/"
                            onChange={e => onChange({ search: e.target.value })}
                            className={fieldClass({ className: cx('w-full pl-9', filters.search.trim() && ACTIVE) })}
                        />
                    </div>
                </div>

                <Button
                    variant="secondary" className="md:hidden"
                    aria-expanded={phoneOpen} aria-controls="book-filters"
                    onClick={() => setPhoneOpen(v => !v)}
                >
                    {set > 0 ? <>Filters <span className="num">({set})</span></> : 'Filters'}
                </Button>

                <div id="book-filters" className={cx('md:contents', phoneOpen ? 'max-md:flex max-md:flex-wrap max-md:items-end max-md:gap-2.5 max-md:w-full' : 'max-md:hidden')}>
                    <Choice
                        id="book-settlement" label="Showing" value={filters.settlement} active={filters.settlement !== DEFAULT_FILTERS.settlement}
                        groups={[{ label: '', options: (Object.keys(SETTLEMENT_LABELS) as SettlementFilter[])
                            .map(value => ({ value, label: SETTLEMENT_LABELS[value] })) }]}
                        onChange={value => onChange({ settlement: value as SettlementFilter })}
                    />
                    <MultiSelect
                        id="book-followup" label="Follow-up" options={FOLLOW_UP_OPTIONS}
                        value={splitList(filters.followUp)}
                        onChange={values => onChange({ followUp: joinList(values) })}
                    />
                    <Choice
                        id="book-ageing" label="Older than" value={filters.ageing} active={filters.ageing !== 'any'}
                        groups={[{ label: '', options: [
                            ...(['any', 'current', 'over45', 'over90', 'over135'] as const).map(value => ({ value, label: AGEING_FILTER_LABELS[value]! })),
                            ...(isOneAgeRow(filters.ageing) ? [{ value: filters.ageing, label: 'One age band' }] : []),
                        ] }]}
                        onChange={value => onChange({ ageing: value as BookFilters['ageing'] })}
                    />
                    {showAssignee && (
                        <MultiSelect
                            id="book-owner" label="Assigned to" anyLabel="Anyone" options={ownerOptions}
                            value={splitList(filters.owner)}
                            onChange={values => onChange({ owner: joinList(values) })}
                        />
                    )}
                    <Choice
                        id="book-sort" label="Sort by" value={sort} active={false}
                        groups={[{ label: '', options: (Object.keys(SORT_LABELS) as BookSort[])
                            .map(value => ({ value, label: SORT_LABELS[value] })) }]}
                        onChange={value => onChange({ sort: value as BookSort })}
                    />

                    <div className="flex items-center gap-1">
                        <Button
                            variant="quiet"
                            aria-expanded={open}
                            aria-controls="book-more-filters"
                            onClick={() => setOpen(v => !v)}
                        >
                            {open ? 'Fewer filters' : folded > 0 ? <>More filters <span className="num">({folded})</span></> : 'More filters'}
                        </Button>
                        {narrowed > 0 && (
                            <Button variant="quiet" onClick={onClear}>
                                Clear all <span className="num">({narrowed})</span>
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div id="book-more-filters" hidden={!open} className="px-4 py-3 border-t border-separator bg-card-2">
                <div className="flex items-end gap-2.5 flex-wrap">
                    <MultiSelect
                        id="book-category" label="Category" options={categoryOptions}
                        value={filters.category}
                        onChange={values => onChange({ category: values })}
                    />
                    <MultiSelect
                        id="book-rank" label="Rank" options={RANK_OPTIONS}
                        value={splitList(filters.rank)}
                        onChange={values => onChange({ rank: joinList(values) })}
                    />
                    <Choice
                        id="book-contact" label="Last contact" value={filters.contact} active={filters.contact !== 'any'}
                        groups={[{ label: '', options: (Object.keys(CONTACT_FILTER_LABELS) as ContactFilter[])
                            .map(value => ({ value, label: CONTACT_FILTER_LABELS[value] })) }]}
                        onChange={value => onChange({ contact: value as ContactFilter })}
                    />
                    <label className="flex items-center gap-2 h-9 text-body text-label cursor-pointer">
                        <input
                            type="checkbox"
                            checked={filters.urgentOnly}
                            onChange={e => onChange({ urgentOnly: e.target.checked })}
                        />
                        Urgent only
                    </label>
                </div>
            </div>
        </div>
    );
}

/** A filter's label: small, above the control, so a row of filters reads as one toolbar. */
const FILTER_LABEL = filterLabelClass;

/** A control that is narrowing the list says so on itself. */
const ACTIVE = '!border-accent bg-accent-tint';

function Choice({
    id, label, value, groups, active, onChange,
}: {
    id: string; label: string; value: string; active: boolean;
    groups: { label: string; options: { value: string; label: string }[] }[];
    onChange: (value: string) => void;
}) {
    return (
        <div>
            <label htmlFor={id} className={FILTER_LABEL}>{label}</label>
            <select
                id={id} value={value} onChange={e => onChange(e.target.value)}
                className={fieldClass({ className: cx(active && ACTIVE) })}
            >
                {groups.map((g, i) => g.label
                    ? <optgroup key={g.label} label={g.label}>{g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</optgroup>
                    : g.options.map(o => <option key={`${i}-${o.value}`} value={o.value}>{o.label}</option>))}
            </select>
        </div>
    );
}

/**
 * The figure at the top of the list, and what it is allowed to claim.
 *
 * A financial product must never show a page subtotal where a receivables
 * total belongs. Every filter runs in the database over the caller's whole
 * visible book, so `book_totals` answers the same question the list shows and
 * the headline is a real total whatever is filtered. The second shape survives
 * only because a request can fail: when the aggregate did not arrive, the
 * screen says "Visible amount" rather than putting "total" over a number that
 * is not one.
 */
function BookSummary({
    serverTotals,
    onScreen,
    loadedCount,
    loading,
}: {
    serverTotals: ServerTotals | null;
    onScreen: { count: number; outstanding: number };
    loadedCount: number;
    loading: boolean;
}) {
    const { money, count } = useFormatters();

    // Not yet answered is not the same as failed.
    if (!serverTotals && loading) {
        return (
            <div className="text-right" role="status">
                <div className="text-caption text-label-3">Adding up your customers…</div>
            </div>
        );
    }

    if (serverTotals) {
        const shown = loadedCount > 0 && loadedCount < serverTotals.matchedCount;
        return (
            <div className="text-right">
                <div className="text-title font-semibold text-label num leading-tight">
                    {money.exact(serverTotals.outstanding)}
                </div>
                <div className="text-caption text-label-3">
                    Total outstanding ·{' '}
                    {count(serverTotals.matchedCount)}{' '}
                    {serverTotals.matchedCount === 1 ? 'account' : 'accounts'}
                    {serverTotals.matchedCount !== serverTotals.visibleCount && (
                        <> of {count(serverTotals.visibleCount)}</>
                    )}
                </div>
                {shown && (
                    <div className="text-caption text-label-3 mt-0.5">
                        Showing {count(loadedCount)} of {count(serverTotals.matchedCount)}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="text-right">
            <div className="text-heading font-semibold text-label num">
                {money.exact(onScreen.outstanding)}
            </div>
            <div className="text-caption text-label-3">
                {/* Never "total": the sum of what is on screen, and it says so. */}
                Visible amount · {count(onScreen.count)}{' '}
                {onScreen.count === 1 ? 'account' : 'accounts'} shown
            </div>
            <div className="text-caption text-warn mt-0.5 max-w-64">
                We could not add up all your customers just now, so this is the amount on screen.
            </div>
        </div>
    );
}
